/**
 * UYAP hata yönlendirmeleri — merkezi sözlük (registry).
 *
 * Amaç: UYAP'tan dönen ham/teknik hataları tek bir yerden tanıyıp, kullanıcıya
 * ham metin yerine yönlendirici mesaj göstermek.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  YENİ HATA EKLEMEK İÇİN: Yalnızca aşağıdaki UYAP_ERROR_YONLENDIRMELERI
 *  dizisine bir kural nesnesi ekleyin. Çağıran kod (ör. tevziAl() catch blokları)
 *  değişmez; matchUyapError() otomatik olarak yeni kuralı da dener.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * İlgili: DEV-1902 / TDT-2707.
 */

export interface UyapErrorYonlendirme {
    /** Tanımlayıcı anahtar (loglama/test amaçlı; kullanıcıya gösterilmez). */
    key: string;
    /**
     * UYAP hata kodları. Hatanın errorCode'u (veya response.data.errorCode)
     * bu listedeyse kural eşleşir.
     */
    errorCodes: string[];
    /** Kullanıcıya gösterilecek yönlendirici mesaj. */
    userMessage: string;
}

/**
 * Yönlendirme sözlüğü. Kurallar sırayla denenir; ilk eşleşen kural döner.
 */
export const UYAP_ERROR_YONLENDIRMELERI: UyapErrorYonlendirme[] = [
    {
        key: 'TAMAMLANMAYAN_DOSYA',
        // TDT-2707 / DEV-1902 — UYAP "Tamamlanmayan Dosyalar" bekleyen kayıt varsa TevziAl reddi.
        errorCodes: ['PRTL_AVKT_10105'],
        userMessage:
            'Takip açılamadı — UYAP hesabınızın "Tamamlanmayan Dosyalar" bölümünde ' +
            'bekleyen kayıt bulunduğu için UYAP yeni takip açılmasına izin vermiyor. ' +
            'Bu, e-İcraPro kaynaklı bir sorun değildir; uyarı doğrudan UYAP tarafından gönderilmektedir.\n\n' +
            'Ne yapmalısınız:\n' +
            '1) UYAP Avukat Portalı > İcra > "Tamamlanmayan Dosyalar" ekranını açın.\n' +
            '2) Bekleyen dosyaları tamamlayın (aç/ödeme yap) veya iptal edin.\n' +
            '3) Yeni oluşan bir kayıt UYAP tarafından ilk 10 dakika silinemez; bu durumda kısa süre bekleyip tekrar deneyin.\n' +
            '4) Bölüm boşaldıktan sonra "Tevzi Al" işlemini yeniden başlatın.',
    },
    {
        key: 'ADRES_LIMITI',
        // Kişi/kurum için tanımlanabilecek maksimum adres sayısına ulaşıldı.
        errorCodes: ['SYS_GNL_10274'],
        userMessage:
            'UYAP tarafında kişi/kurum için tanımlanabilecek maksimum adres sayısına ulaşıldığı için ' +
            'yeni adres eklenememektedir. İşleme devam edebilmek için Detay ekranından UYAP kaynaklı ' +
            'mevcut bir adres seçerek devam ediniz.',
    },
    {
        key: 'EVRAK_GONDERME_AKSAKLIK',
        // davaAcilisEvrakGonderme_brd.ajx — UYAP kaynaklı geçici evrak kaydetme aksaklığı.
        errorCodes: ['PRTL_GNL_100044'],
        userMessage:
            'UYAP sistemlerinde yaşanan aksaklık sebebiyle işlemlerinizi daha sonra tekrar deneyebilirsiniz.',
    },
    {
        key: 'BAROKART_BAKIYE_YETERSIZ',
        // davaAcilisOdemeIslemleri_brd.ajx — Barokart (e-barobirlik) bakiyesi yetersiz.
        errorCodes: ['PRTL_GNL_100025-1'],
        userMessage:
            'Barokart (e-barobirlik) hesabınızda yeterli bakiye bulunmadığından ödeme işlemi tamamlanamadı.\n ' +
            'Tevzi işleminiz alınmış olabileceğinden, UYAP Portal üzerinden ' +
            '"Tamamlanmayan Dosyalar / İcra Takip Tamamlanmayan Dosyalar" bölümüne giderek ilgili dosyanızda ' +
            'ödemenin gerçekleşip gerçekleşmediğini kontrol ediniz. Ödeme yapılmamışsa, işleminizi aynı bölümden ' +
            'Vakıfbank ile tamamlayabilirsiniz.',
    },
    {
        key: 'ADRES_IL_ILCE_EKSIK',
        // icra_takip_tevzi_islemleri.ajx — UYAP/XML yazışma adresi il/ilçe bilgisi eksik.
        errorCodes: ['YRG_TRF_10126'],
        userMessage:
            'UYAP\'taki veya XML\'deki adres bilgilerinde (il/ilçe) eksiklik bulunduğu için tevzi işlemi tamamlanamadı.\n ' +
            'İlgili adresi, bu dosyanın sağ tarafındaki "Ön İzleme" butonuna tıklayarak açılan ekrandan düzeltip ' +
            'işlemi yeniden deneyebilirsiniz.',
    },
    // ── Yeni yönlendirmeler buraya eklenir ──
    // {
    //     key: 'ORNEK_HATA',
    //     errorCodes: ['PRTL_XXX_00000'],
    //     userMessage: 'Kullanıcıya gösterilecek yönlendirici mesaj...',
    // },
];

/**
 * Bir hatayı sözlükteki kurallara karşı dener; errorCode'u eşleşen ilk
 * yönlendirmeyi döner, yoksa null.
 *
 * UyapRequest iş-mantığı hatalarında reject objesi düz { error, errorCode, message }
 * biçimindedir (HTTP response taşımaz); axios kaynaklı hatalarda ise response.data
 * bulunur. Her iki şekildeki errorCode da kontrol edilir.
 */
export function matchUyapError(e: any): UyapErrorYonlendirme | null {
    const code = e?.errorCode ?? e?.response?.data?.errorCode;
    if (!code) return null;

    for (const rule of UYAP_ERROR_YONLENDIRMELERI) {
        if (rule.errorCodes.includes(code)) {
            return rule;
        }
    }

    return null;
}
